import * as React from "react";
import LoadingSpinner from "@/components/LoadingSpinner";
import useApi from "@/hooks/useApi";

interface StuckJob {
  id: string;
  method: string;
  startedAt: string;
  stuckForMinutes: number;
  serverId: string;
}

interface ServerInfo {
  Name: string;
  WorkersCount: number;
  startedAt: string;
  heartbeat: string;
  queues: string[];
}

interface HangfireStatus {
  serverCount: number;
  servers: ServerInfo[];
  enqueued: number;
  processing: number;
  failed: number;
  scheduled: number;
  succeeded: number;
  stuckJobCount: number;
  stuckJobs: StuckJob[];
  dashboardUrl: string;
  checkedAt: string;
}

function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-5 flex flex-col gap-1">
      <div className={`text-3xl font-bold ${color}`}>{value.toLocaleString()}</div>
      <div className="text-sm text-slate-500">{label}</div>
    </div>
  );
}

export default function HangfireMonitor() {
  const api = useApi();
  const [status, setStatus] = React.useState<HangfireStatus | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [actionMsg, setActionMsg] = React.useState<string | null>(null);
  const [actioning, setActioning] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const res = await api.messaging.get("superadmin/hangfire/status");
      const json = await res.json();
      if (json && typeof json.serverCount === "number") setStatus(json);
    } catch {
      /* non-fatal */
    } finally {
      setLoading(false);
    }
  }, [api]);

  React.useEffect(() => {
    load();
    const iv = setInterval(load, 30_000);
    return () => clearInterval(iv);
  }, [load]);

  async function handleRequeueStuck() {
    setActioning(true);
    setActionMsg(null);
    try {
      const res = await api.messaging.post("superadmin/hangfire/requeue-stuck", {});
      const json = await res.json();
      setActionMsg(json.message ?? "Done.");
      await load();
    } catch {
      setActionMsg("Request failed.");
    } finally {
      setActioning(false);
    }
  }

  async function handleClearFailed() {
    if (!confirm(`Clear all ${status?.failed ?? 0} failed jobs? This cannot be undone.`)) return;
    setActioning(true);
    setActionMsg(null);
    try {
      const res = await api.messaging.post("superadmin/hangfire/clear-failed", {});
      const json = await res.json();
      setActionMsg(json.message ?? "Done.");
      await load();
    } catch {
      setActionMsg("Request failed.");
    } finally {
      setActioning(false);
    }
  }

  if (loading && !status) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  const isHealthy = (status?.serverCount ?? 0) > 0;
  const hasStuck = (status?.stuckJobCount ?? 0) > 0;
  const hasFailed = (status?.failed ?? 0) > 0;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Hangfire Monitor</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Background job queue health — refreshes every 30s
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={load}
            className="text-sm text-blue-600 underline"
          >
            Refresh
          </button>
          <a
            href={`${import.meta.env.VITE_MESSAGING_API_URL}/hangfire`}
            target="_blank"
            rel="noreferrer"
            className="text-sm px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700"
          >
            Full Dashboard ↗
          </a>
        </div>
      </div>

      {/* Health banner */}
      <div
        className={`rounded-xl p-4 flex items-center gap-3 ${
          isHealthy
            ? hasStuck
              ? "bg-amber-50 border border-amber-200"
              : "bg-green-50 border border-green-200"
            : "bg-red-50 border border-red-300"
        }`}
      >
        <span className="text-2xl">
          {isHealthy ? (hasStuck ? "⚠️" : "✅") : "🔴"}
        </span>
        <div>
          <div className="font-semibold text-slate-800">
            {isHealthy
              ? hasStuck
                ? `${status!.stuckJobCount} stuck job(s) detected`
                : "Hangfire is healthy"
              : "CRITICAL: No Hangfire servers"}
          </div>
          <div className="text-xs text-slate-500">
            {status?.serverCount ?? 0} server(s) active · checked{" "}
            {status ? new Date(status.checkedAt).toLocaleTimeString() : "—"}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard label="Servers" value={status?.serverCount ?? 0} color={isHealthy ? "text-green-600" : "text-red-600"} />
        <StatCard label="Enqueued" value={status?.enqueued ?? 0} color="text-blue-600" />
        <StatCard label="Processing" value={status?.processing ?? 0} color="text-indigo-600" />
        <StatCard label="Stuck (>10m)" value={status?.stuckJobCount ?? 0} color={hasStuck ? "text-amber-600" : "text-slate-400"} />
        <StatCard label="Failed" value={status?.failed ?? 0} color={hasFailed ? "text-red-600" : "text-slate-400"} />
        <StatCard label="Scheduled" value={status?.scheduled ?? 0} color="text-slate-600" />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 items-center">
        <button
          onClick={handleRequeueStuck}
          disabled={actioning || !hasStuck}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            hasStuck && !actioning
              ? "bg-amber-500 hover:bg-amber-600 text-white"
              : "bg-slate-100 text-slate-400 cursor-not-allowed"
          }`}
        >
          {actioning ? "Working…" : `Requeue Stuck (${status?.stuckJobCount ?? 0})`}
        </button>
        <button
          onClick={handleClearFailed}
          disabled={actioning || !hasFailed}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            hasFailed && !actioning
              ? "bg-red-500 hover:bg-red-600 text-white"
              : "bg-slate-100 text-slate-400 cursor-not-allowed"
          }`}
        >
          {actioning ? "Working…" : `Clear Failed (${status?.failed ?? 0})`}
        </button>
        {actionMsg && (
          <span className="text-sm text-green-700 bg-green-50 px-3 py-1.5 rounded-lg">
            {actionMsg}
          </span>
        )}
      </div>

      {/* Stuck jobs table */}
      {hasStuck && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800">Stuck Processing Jobs</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Jobs in Processing state for more than 10 minutes — likely orphaned by a Redis timeout or crashed worker.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-xs">
                  <th className="text-left px-5 py-3">Job ID</th>
                  <th className="text-left px-5 py-3">Method</th>
                  <th className="text-left px-5 py-3">Started At</th>
                  <th className="text-left px-5 py-3">Stuck For</th>
                  <th className="text-left px-5 py-3">Server</th>
                </tr>
              </thead>
              <tbody>
                {status!.stuckJobs.map((job) => (
                  <tr key={job.id} className="border-t border-slate-50 hover:bg-slate-50">
                    <td className="px-5 py-3 font-mono text-xs text-slate-500">{job.id}</td>
                    <td className="px-5 py-3 font-mono text-xs max-w-xs truncate" title={job.method}>
                      {job.method}
                    </td>
                    <td className="px-5 py-3 text-slate-600">
                      {new Date(job.startedAt).toLocaleString()}
                    </td>
                    <td className="px-5 py-3">
                      <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded">
                        {job.stuckForMinutes}m
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-400 font-mono truncate max-w-xs" title={job.serverId}>
                      {job.serverId}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Server list */}
      {status && status.servers && status.servers.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800">Active Servers</h2>
          </div>
          <div className="divide-y divide-slate-50">
            {status.servers.map((srv) => (
              <div key={srv.Name} className="px-5 py-3 flex items-center gap-6 text-sm">
                <div className="flex-1 font-mono text-xs text-slate-600 truncate" title={srv.Name}>
                  {srv.Name}
                </div>
                <div className="text-slate-500">{srv.WorkersCount} workers</div>
                <div className="text-slate-400 text-xs">
                  ♥ {srv.heartbeat ? new Date(srv.heartbeat).toLocaleTimeString() : "—"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
